import { EditableText, defineBlock } from "eddev/blocks"
import { Col, Container, Grid } from "@components/layout/Grid"
import { cn } from "@utils/tw"
import { Typography } from "@components/atoms/Typography"
import { useIsMobile } from "@hooks/useViewportDerived"
import { VSpace } from "@components/layout/Spacer"
import { ComponentPropsWithoutRef, forwardRef } from "react"

export const meta: BlockMeta = {
  title: "Pill list",
  category: "common",
  icon: "editor-ul",
  keywords: ["pill list"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
  },
}

export default defineBlock("general/pill-list", (props) => {
  const isMobile = useIsMobile()

  return (
    <ListContainer>
      <Grid>
        {!isMobile && !env.admin && <Col initial={12} tablet={1} laptop={2} />}
        <Col initial={12} tablet={env.admin ? 12 : 10} laptop={env.admin ? 12 : 8}>
          <EditableText as="h4" store="title" placeholder="Enter title..." />
          <VSpace initial={4} />
          {props.items && (
            <ul>
              {props.items.map((item, i) => (
                <PillLi key={i} data-pill-text={item?.pillText}>
                  <Typography as="span" variant="text-m" dangerouslySetInnerHTML={{ __html: item?.content ?? "" }} />
                </PillLi>
              ))}
            </ul>
          )}
        </Col>
        {!isMobile && !env.admin && <Col initial={12} tablet={1} laptop={2} />}
      </Grid>
    </ListContainer>
  )
})

const LIST_CONTAINER_STYLES = cn(
  "my-6",
  "[&_h4]:type-title-s [&_h4]:my-0",
  "[&_ul]:list-none [&_ul]:m-0 [&_ul]:p-0",
  "[&_li]:my-5 [&_li]:mx-0 [&_li]:pt-0 [&_li]:pr-0 [&_li]:pb-5 [&_li]:pl-[100px]",
  "[&_li]:flex [&_li]:relative [&_li]:border-b [&_li]:border-b-light-grey",
  "[&_li]:before:absolute [&_li]:before:flex [&_li]:before:items-center [&_li]:before:justify-center",
  "[&_li]:before:text-center [&_li]:before:no-underline [&_li]:before:type-label-caps",
  "[&_li]:before:h-5 [&_li]:before:rounded-[10px] [&_li]:before:px-2 [&_li]:before:bg-yellow [&_li]:before:text-black",
  "[&_li]:before:left-0 [&_li]:before:top-1 [&_li]:before:w-[50px]",
  // Replaces the stitches `css` escape hatch's per-item dynamic
  // `content: "<pillText>"` — attr() reads the data attribute at render
  // time, so this one static class covers every item's differing text.
  "[&_li]:before:content-[attr(data-pill-text)]",
)

const ListContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Container>>(
  ({ className, ...props }, ref) => <Container ref={ref} className={cn(LIST_CONTAINER_STYLES, className)} {...props} />,
)
ListContainer.displayName = "ListContainer"

const PillLi = forwardRef<HTMLLIElement, ComponentPropsWithoutRef<"li">>(({ className, ...props }, ref) => (
  <li ref={ref} className={className} {...props} />
))
PillLi.displayName = "PillLi"
